import posthog from "posthog-js";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://app.posthog.com";
const isBrowser = typeof window !== "undefined";

type EventPayload = {
  event: string;
  properties?: Record<string, unknown>;
};

let initialized = false;
let waitingForConsent = false;
const queue: EventPayload[] = [];
const readyCallbacks: Array<() => void> = [];

const getReferrerDomain = (referrer: string | null) => {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname;
  } catch {
    return null;
  }
};

const readIubendaConsent = () => {
  if (!isBrowser) return null;
  try {
    const stored = localStorage.getItem("iubenda_consent");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const hasAnalyticsConsent = () => {
  if (!isBrowser) return false;

  // Prefer live consent data if available
  const consent = (window as unknown as { _iub?: any })._iub?.cs?.consent;
  if (consent?.purposes?.analytics === true) return true;
  if (consent?.purposes?.statistics === true) return true;
  if (consent?.analytics === true) return true;

  // Fallback to stored consent snapshot
  const storedConsent = readIubendaConsent();
  if (storedConsent?.purposes?.analytics === true) return true;
  if (storedConsent?.purposes?.statistics === true) return true;
  if (storedConsent?.analytics === true) return true;

  // If no structured purposes exist, require an explicit positive flag
  return false;
};

const onConsentGranted = (callback: () => void) => {
  if (!isBrowser) return;
  const handler = () => {
    if (hasAnalyticsConsent()) {
      document.removeEventListener("iubenda-consent-change", handler as EventListener);
      document.removeEventListener("iubenda-banner-closed", handler as EventListener);
      callback();
    }
  };

  document.addEventListener("iubenda-consent-change", handler as EventListener);
  document.addEventListener("iubenda-banner-closed", handler as EventListener);
};

const flushQueue = () => {
  if (!initialized) return;
  while (queue.length) {
    const payload = queue.shift();
    if (payload) {
      posthog.capture(payload.event, payload.properties);
    }
  }
};

const runReadyCallbacks = () => {
  if (!initialized) return;
  while (readyCallbacks.length) {
    const cb = readyCallbacks.shift();
    try {
      cb?.();
    } catch {
      // no-op; avoid crashing init pipeline
    }
  }
};

const registerAttributionContext = () => {
  if (!isBrowser || !initialized) return;

  const url = new URL(window.location.href);
  const referrer = document.referrer || null;
  const utmParams: Record<string, string> = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((key) => {
    const value = url.searchParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  const dubId = url.searchParams.get("dub_id");

  posthog.register_once({
    source: "marketing",
    page: window.location.pathname,
    referrer,
    referrer_domain: getReferrerDomain(referrer),
    ...(dubId ? { dub_id: dubId } : {}),
    ...utmParams,
  });
};

export const ensureAnalytics = () => {
  if (initialized) return true;
  if (!POSTHOG_KEY || !isBrowser) return false;

  if (!hasAnalyticsConsent()) {
    if (!waitingForConsent) {
      waitingForConsent = true;
      onConsentGranted(() => {
        waitingForConsent = false;
        ensureAnalytics();
      });
    }
    return false;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST.replace(/\/+$/, ""),
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    persistence: "localStorage+cookie",
    disable_session_recording: true,
  });
  posthog.opt_in_capturing();
  initialized = true;
  registerAttributionContext();
  flushQueue();
   runReadyCallbacks();
  return true;
};

export const identifyUser = (userId: string, traits?: Record<string, unknown>) => {
  if (!userId) return;
  ensureAnalytics();
  if (!initialized) {
    readyCallbacks.push(() => {
      posthog.identify(userId, traits);
    });
    return;
  }
  posthog.identify(userId, traits);
};

export const registerContext = (props: Record<string, unknown>) => {
  ensureAnalytics();
  if (!initialized) {
    readyCallbacks.push(() => {
      posthog.register(props);
    });
    return;
  }
  posthog.register(props);
};

export const trackEvent = (event: string, properties?: Record<string, unknown>) => {
  const payload: EventPayload = { event, properties };
  if (!ensureAnalytics()) {
    queue.push(payload);
    return;
  }
  posthog.capture(event, properties);
};

export const resetAnalytics = () => {
  // Always clear queues and callbacks regardless of init state to avoid replaying stale identities
  queue.length = 0;
  readyCallbacks.length = 0;
  if (!initialized) return;
  posthog.reset();
  initialized = false;
};
